import { Component, memo, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { processText, svgToDataUrl } from "../../../../utils/learnUtils";
import { useMathJax } from "../../../../hooks/useMathJax";
import { useCaptionHighlight } from "../../../../hooks/useCaptionHighlight";
import { useLearnCtx } from "../.././LearnPage";
import SelectionToolbar from "../SelectionToolbar";
import IntFigCheckbox from "./IntFigCheckbox";

/* ── Error boundary — see IntFigureComp.jsx for rationale; resets when
   `item` changes identity (a real content edit) but not on interaction-only
   re-renders (checkbox toggles, timeline ticks). ── */
class IntFigureErrorBoundary extends Component {
  state = { error: null, item: null };

  static getDerivedStateFromProps(props, state) {
    if (props.item !== state.item) {
      return { error: null, item: props.item };
    }
    return null;
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <div className="ct__intfig-render-error">
          ⚠ This interactive figure could not be displayed (invalid data).
        </div>
      );
    }
    return this.props.children;
  }
}

/* ── Sliders icon (shared with IntFigureComp) ── */
function SlidersIcon() {
  return (
    <svg
      className="ct__intfig-instr-icon"
      viewBox="0 0 16 14"
      fill="none"
      aria-hidden="true"
    >
      <line
        x1="1"
        y1="2"
        x2="15"
        y2="2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="5" cy="2" r="1.5" fill="currentColor" />
      <line
        x1="1"
        y1="7"
        x2="15"
        y2="7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="10" cy="7" r="1.5" fill="currentColor" />
      <line
        x1="1"
        y1="12"
        x2="15"
        y2="12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="6" cy="12" r="1.5" fill="currentColor" />
    </svg>
  );
}

/**
 * Interactive canvas for the discrete figure — reused in both normal and modal views.
 * The background image (index 0 when multitype==="background") is absolutely positioned
 * beneath the state image so it does not affect layout flow.
 */
function DiscreteCanvas({
  images,
  imageIndex,
  hasBackground,
  components,
  params,
  setParam,
  style,
}) {
  const safeIndex = Number.isFinite(imageIndex)
    ? imageIndex
    : hasBackground
      ? 1
      : 0;
  return (
    <div className="ct__intfig-canvas" style={style}>
      {hasBackground && (
        <img
          src={svgToDataUrl(images[0])}
          alt=""
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "auto",
          }}
        />
      )}
      <img
        src={svgToDataUrl(images[safeIndex] ?? images[0])}
        alt="Interactive figure"
        className="ct__intfig-bg"
      />
      {components?.map((c, idx) => {
        if (c.type !== "EDCOMP_INT_CHECKBOX") return null;
        const paramId = c.param ?? idx;
        return (
          <div
            key={c._id ?? idx}
            className="ct__intfig-ov"
            style={{
              left: c.left,
              top: c.top,
              width: c.width,
              height: c.height,
              zIndex: (c.z_index ?? 0) + 10,
            }}
          >
            <IntFigCheckbox
              checked={params[paramId] === 1}
              onChange={(v) => setParam(paramId, v ? 1 : 0)}
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * Renders an EDCOMP_INT_FIGURE_DISCRETE: a figure whose displayed image is selected
 * by a set of checkboxes via a binary-state → image-index mapping.
 * @param {{ item: object, showExpand?: boolean }} props
 */
function IntFigureDiscreteComp({ item, showExpand = true }) {
  const { data } = item;
  const { openSupport, openBookmarkModal } = useLearnCtx();

  const images = (data.image ?? "").split(";;;split;;;");
  const mapping = (data.mapping ?? "0").split(",");
  const hasBackground = data.multitype === "background";

  /* ── Params: one slot per checkbox, indexed by c.param ── */
  const [params, setParamsState] = useState(() => {
    const p = new Array(9).fill(0);
    data.components?.forEach((c) => {
      if (c.type === "EDCOMP_INT_CHECKBOX") {
        p[c.param ?? 0] = c.initial_value ?? 0;
      }
    });
    return p;
  });

  /* ── Binary-weighted state → image index ── */
  const stateIndex = Math.min(
    Math.round(params.reduce((acc, v, i) => acc + +v * Math.pow(2, i), 0)),
    mapping.length - 1,
  );
  const imageIndex = Number(mapping[stateIndex]);

  const setParam = useCallback((paramId, value) => {
    setParamsState((prev) => {
      const next = [...prev];
      next[parseInt(paramId)] = value;
      return next;
    });
  }, []);

  /* ── Expand / modal ── */
  const [isExpanded, setIsExpanded] = useState(false);
  const [modalCanvasStyle, setModalCanvasStyle] = useState(null);

  /* ── Caption processing ── */
  const captionRef = useRef(null);
  const modalCaptionRef = useRef(null);
  const rawCaption = data.text ?? "";
  const captionHtml = processText(rawCaption);

  const inlineHL = useCaptionHighlight(captionRef, item._id);
  const modalHL = useCaptionHighlight(modalCaptionRef, item._id);

  useMathJax(captionRef, [rawCaption], inlineHL.onTypesetRef);
  useMathJax(modalCaptionRef, [rawCaption, isExpanded], modalHL.onTypesetRef);

  /* ── Size the modal canvas to fit the viewport ── */
  useEffect(() => {
    if (!isExpanded) {
      setModalCanvasStyle(null);
      return;
    }
    const refImg = images[hasBackground ? 1 : 0] ?? images[0];
    const measurer = new window.Image();
    measurer.onload = () => {
      const { naturalWidth: nw, naturalHeight: nh } = measurer;
      if (!nw || !nh) return;
      const fs = parseFloat(
        getComputedStyle(document.documentElement).fontSize,
      );
      const innerPad = fs * 2.5;
      const captionMaxH = rawCaption
        ? Math.max(60, Math.round((rawCaption.length / 1000) * 250))
        : 0;
      const captionOverhead = rawCaption ? captionMaxH + fs : 0;
      const maxW = window.innerWidth * 0.85 - innerPad;
      const maxH =
        window.innerHeight * 0.85 - innerPad - captionOverhead - fs * 3;
      const scale = Math.min(maxW / nw, maxH / nh);
      setModalCanvasStyle({
        width: `${Math.round(nw * scale)}px`,
        height: `${Math.round(nh * scale)}px`,
      });
    };
    measurer.src = svgToDataUrl(refImg);
  }, [isExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Modal caption: support links close modal and open side panel ── */
  const handleModalCaptionClick = useCallback(
    (e) => {
      const link = e.target.closest("[data-support-id]");
      if (!link) return;
      e.preventDefault();
      setIsExpanded(false);
      openSupport(link.dataset.supportId);
    },
    [openSupport],
  );

  /* ── Keyboard close ── */
  useEffect(() => {
    if (!isExpanded) return;
    const onKey = (e) => {
      if (e.key === "Escape") setIsExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isExpanded]);

  const figureWidth = data.width ?? "100%";
  const isFullWidth = figureWidth === "100%";
  const canvasProps = {
    images,
    imageIndex,
    hasBackground,
    components: data.components,
    params,
    setParam,
  };

  const captionBody = (
    <>
      {rawCaption && <span dangerouslySetInnerHTML={{ __html: captionHtml }} />}
      {data.instructions && (
        <>
          {rawCaption && <br />}
          <span className="ct__intfig-instr">
            <SlidersIcon />
            <strong
              dangerouslySetInnerHTML={{
                __html: processText(data.instructions),
              }}
            />
          </span>
        </>
      )}
    </>
  );

  return (
    <>
      <figure
        id={"intfig:" + item._id}
        className={`ct__intfig${isFullWidth ? " ct__intfig--full" : " ct__intfig--partial"}`}
      >
        <div className="ct__intfig-canvas-wrap">
          <DiscreteCanvas {...canvasProps} style={{ width: figureWidth }} />
        </div>

        {showExpand && (
          <button
            className="ct__figure-expand"
            onClick={() => setIsExpanded(true)}
            aria-label="Expand figure"
            title="Expand figure"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M2 2h4M2 2v4M12 2H8M12 2v4M2 12h4M2 12V8M12 12H8M12 12V8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}

        {(rawCaption || data.instructions) && (
          <figcaption
            ref={captionRef}
            className="ct__figure-caption ct__intfig-caption"
          >
            {captionBody}
          </figcaption>
        )}

        {inlineHL.toolbarPos && (
          <SelectionToolbar
            style={inlineHL.toolbarStyle}
            onCopy={inlineHL.copySelection}
            copyDone={inlineHL.copyDone}
            onPointerDown={inlineHL.preventDeselect}
            onBookmark={() =>
              openBookmarkModal(item._id, item.title, "intfig:" + item._id)
            }
            onHighlight={inlineHL.handleHighlight}
            onRemoveHighlight={inlineHL.handleRemoveHighlight}
          />
        )}
      </figure>

      {/* Modal — portalled to document.body so position:fixed is viewport-relative */}
      {isExpanded &&
        createPortal(
          <div
            className="ct__intfig-modal-backdrop"
            onClick={(e) => {
              if (e.target === e.currentTarget) setIsExpanded(false);
            }}
          >
            <div className="ct__intfig-modal">
              <button
                className="ct__intfig-modal-close"
                onClick={() => setIsExpanded(false)}
                aria-label="Close"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M1 1l10 10M11 1L1 11"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              <DiscreteCanvas
                {...canvasProps}
                style={modalCanvasStyle ?? { maxWidth: "85vw" }}
              />

              {(rawCaption || data.instructions) && (
                <div
                  ref={modalCaptionRef}
                  className="ct__figure-caption ct__intfig-modal-caption"
                  onClick={handleModalCaptionClick}
                >
                  {captionBody}
                </div>
              )}

              {modalHL.toolbarPos && (
                <SelectionToolbar
                  style={modalHL.toolbarStyle}
                  onCopy={modalHL.copySelection}
                  copyDone={modalHL.copyDone}
                  onPointerDown={modalHL.preventDeselect}
                  onBookmark={() =>
                    openBookmarkModal(
                      item._id,
                      item.title,
                      "intfig:" + item._id,
                    )
                  }
                  onHighlight={modalHL.handleHighlight}
                  onRemoveHighlight={modalHL.handleRemoveHighlight}
                />
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

export default memo(function IntFigureDiscreteCompSafe(props) {
  return (
    <IntFigureErrorBoundary item={props.item}>
      <IntFigureDiscreteComp {...props} />
    </IntFigureErrorBoundary>
  );
});
